import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useUrlState } from '@/lib/useUrlState';
import { useParentShell } from './ParentShell';

/**
 * Внеурочные занятия — календарь проведённых сессий по выбранному
 * месяцу. Без баланса (внеурочка бесплатная).
 *
 * Клик по плашке/строке занятия — модалка с темой, педагогом, оценкой
 * и комментарием (если педагог их выставил).
 *
 * Стили заточены под `pc-extras-*` (тот же визуальный язык, что у
 * «Дополнительные занятия»): на десктопе — месячная сетка, на мобиле —
 * агенда-лента.
 */
const URL_SCHEMA = { month: '', open: '' };

const MONTHS_RU = [
  'Январь','Февраль','Март','Апрель','Май','Июнь',
  'Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь',
];
const DOW_LABELS = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];
const DOW_FULL_RU = ['понедельник','вторник','среда','четверг','пятница','суббота','воскресенье'];

function fmtDateRu(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}
function monthAdd(ym, n) {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}
function dayOfWeek(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.getDay() || 7;
}

export default function ParentExtracurricularPage() {
  const { child, activeYear } = useParentShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);

  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState(null);

  // Окно выбора внеурочных занятий (если завуч открыл период и идёт сбор
  // заявок — сегодня в [date_start_selection … date_end]). Вне окна
  // endpoint отдаёт period=null и страница выглядит как обычно.
  const [selection, setSelection] = useState(null);
  useEffect(() => {
    if (!child) { setSelection(null); return; }
    let cancelled = false;
    api.get('/api/parent/extracurricular-selection?student_id=' + child.id)
      .then((res) => { if (!cancelled) setSelection(res.period ? res : null); })
      .catch(() => { if (!cancelled) setSelection(null); });
    return () => { cancelled = true; };
  }, [child?.id]);

  useEffect(() => {
    if (!child || !activeYear) return;
    let cancelled = false;
    setLoad(true); setError(null);
    const qs = new URLSearchParams({ student_id: String(child.id), year_id: String(activeYear.id) });
    if (params.month) qs.set('month', params.month);
    api.get('/api/parent/extracurricular?' + qs.toString())
      .then((res) => {
        if (cancelled) return;
        setData(res);
        if (!params.month && res.month) setParams({ month: res.month });
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child?.id, activeYear?.id, params.month]);

  // Развернуть selected lesson из URL ?open=sessionId.
  const openId = params.open ? Number(params.open) : 0;
  const openLesson = useMemo(() => {
    if (!openId || !data?.days) return null;
    for (const d of data.days) {
      for (const l of (d.lessons || [])) {
        if (l.id === openId) return { day: d, lesson: l };
      }
    }
    return null;
  }, [openId, data]);

  if (loading) return <div className="pc-body" style={{ color: 'var(--pc-fg-faint)' }}>Загружаем…</div>;
  if (error)   return <div className="pc-body" style={{ color: 'var(--pc-warn)' }}>Ошибка: {error}</div>;
  if (!data)   return null;

  const month = data.month || params.month;
  const summary = data.monthSummary || { count: 0 };
  const days = data.days || [];

  return (
    <div className="pc-body">
      {selection && child && (
        <SelectionCard
          key={child.id + ':' + selection.period.id}
          selection={selection}
          studentId={child.id}
          onSaved={(res) => setSelection((prev) => (prev
            ? { ...prev, selected: res.selected, savedAt: res.savedAt }
            : prev))}
        />
      )}

      <div className="pc-card">
        <div className="pc-card__head">
          <div className="pc-card__title">Внеурочные занятия</div>
          <span className="pc-card__sub">{data.yearStudy?.name}</span>
        </div>
        <div className="pc-card__body" style={{ paddingTop: 6 }}>
          <Calendar
            month={month}
            days={days}
            summary={summary}
            setMonth={(m) => setParams({ month: m, open: '' })}
            onOpen={(l) => setParams({ open: String(l.id) })}
          />
        </div>
      </div>

      {openLesson && (
        <LessonModal
          lesson={openLesson.lesson}
          date={openLesson.day.date}
          onClose={() => setParams({ open: '' })}
        />
      )}
    </div>
  );
}

/* ──────────────────────── Календарь / агенда ──────────────────────── */

function Calendar({ month, days, summary, setMonth, onOpen }) {
  const cells = useMemo(() => {
    if (!days || !days.length) return [];
    const firstDow = dayOfWeek(days[0].date);
    const blanks = Array.from({ length: firstDow - 1 }, () => null);
    return [...blanks, ...days];
  }, [days]);

  if (!month) return null;
  const [y, m] = month.split('-').map(Number);
  const title = `${MONTHS_RU[(m || 1) - 1]} ${y}`;

  return (
    <div className="pc-extras-cal">
      <div className="pc-extras-cal__head">
        <div className="pc-extras-cal__nav">
          <button type="button" className="pc-extras-cal__btn"
                  onClick={() => setMonth(monthAdd(month, -1))} title="Предыдущий месяц">
            <Icon name="chevron-left" size={14} />
          </button>
          <span className="pc-extras-cal__title">{title}</span>
          <button type="button" className="pc-extras-cal__btn"
                  onClick={() => setMonth(monthAdd(month, 1))} title="Следующий месяц">
            <Icon name="chevron" size={14} />
          </button>
        </div>
        {summary && summary.count > 0 && (
          <div className="pc-extras-cal__summary">
            За месяц: <b>{summary.count}</b> зан.
          </div>
        )}
      </div>

      {/* DESKTOP — месячная сетка */}
      <div className="pc-extras-cal__dow">
        {DOW_LABELS.map((d) => <div key={d} className="pc-extras-cal__dow-cell">{d}</div>)}
      </div>
      <div className="pc-extras-cal__grid">
        {cells.map((cell, i) => {
          if (!cell) return <div key={'b' + i} className="pc-extras-cal__cell pc-extras-cal__cell--blank" />;
          const isWeekend = dayOfWeek(cell.date) >= 6;
          const has = (cell.lessons || []).length > 0;
          return (
            <div key={cell.date}
                 className={'pc-extras-cal__cell'
                   + (isWeekend ? ' is-weekend' : '')
                   + (has ? ' has-lessons' : '')}>
              <div className="pc-extras-cal__day">{cell.day}</div>
              {(cell.lessons || []).map((l) => (
                <button key={l.id} type="button"
                        className="pc-extras-cal__lesson pc-extras-cal__lesson--btn"
                        onClick={() => onOpen(l)}
                        title="Подробности занятия">
                  <div className="pc-extras-cal__lesson-subj">{l.subjectName}</div>
                  <div className="pc-extras-cal__lesson-meta">
                    {l.timeStart && <span className="mono">{l.timeStart}</span>}
                    {l.teacherName && <span> · {l.teacherName}</span>}
                  </div>
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {/* MOBILE — агенда */}
      <div className="pc-extras-cal__agenda">
        {days.filter((d) => (d.lessons || []).length > 0).map((d) => (
          <div key={d.date} className="pc-extras-cal__ag-day">
            <div className="pc-extras-cal__ag-date">
              <span className="pc-extras-cal__ag-num">{d.day}</span>
              <span className="pc-extras-cal__ag-dow">{DOW_FULL_RU[(dayOfWeek(d.date) - 1)]}</span>
            </div>
            <div className="pc-extras-cal__ag-lessons">
              {(d.lessons || []).map((l) => (
                <button key={l.id} type="button"
                        className="pc-extras-cal__ag-lesson pc-extras-cal__ag-lesson--btn"
                        onClick={() => onOpen(l)}>
                  <div className="pc-extras-cal__ag-time mono">
                    {l.timeStart}{l.timeEnd ? '–' + l.timeEnd : ''}
                  </div>
                  <div className="pc-extras-cal__ag-body">
                    <div className="pc-extras-cal__ag-subj">{l.subjectName}</div>
                    <div className="pc-extras-cal__ag-meta">
                      {l.teacherName}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {(!days || !days.some((d) => (d.lessons || []).length > 0)) && (
        <div style={{ color: 'var(--pc-fg-faint)', fontSize: 13, padding: '12px 4px' }}>
          В этом месяце проведённых внеурочных занятий пока нет.
        </div>
      )}
    </div>
  );
}

/* ──────────────────────── Модалка темы занятия ──────────────────────── */

function LessonModal({ lesson, date, onClose }) {
  // Закрытие по Escape; запрет скролла под модалкой.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="pc-modal" onClick={(e) => { if (e.target.classList.contains('pc-modal')) onClose(); }}>
      <div className="pc-modal__box" role="dialog" aria-modal="true">
        <div className="pc-modal__head">
          <div>
            <div className="pc-modal__title">{lesson.subjectName || '—'}</div>
            <div className="pc-modal__sub">
              {fmtDateRu(date)}
              {lesson.timeStart && <> · {lesson.timeStart}{lesson.timeEnd ? '–' + lesson.timeEnd : ''}</>}
              {lesson.teacherFull && <> · {lesson.teacherFull}</>}
            </div>
          </div>
          <button type="button" className="pc-modal__close" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="pc-modal__body">
          <div className="pc-modal__field">
            <div className="pc-modal__label">Тема занятия</div>
            <div className="pc-modal__value">
              {lesson.theme
                ? lesson.theme
                : <span className="muted">педагог не указал тему</span>}
            </div>
          </div>
          {lesson.score && (
            <div className="pc-modal__field">
              <div className="pc-modal__label">Оценка / зачёт</div>
              <div className="pc-modal__value">{lesson.score}</div>
            </div>
          )}
          {lesson.comment && (
            <div className="pc-modal__field">
              <div className="pc-modal__label">Комментарий</div>
              <div className="pc-modal__value">{lesson.comment}</div>
            </div>
          )}
        </div>
        <div className="pc-modal__foot">
          <button type="button" className="btn btn--sm" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}

/* ───── Выбор внеурочных занятий (окно сбора заявок) ────────────────────
   Показывается над календарём, когда завуч открыл период и идёт сбор:
   сегодня в [date_start_selection … date_end]. Родитель отмечает предметы
   (мин/макс задаёт завуч) и отправляет. Выбор ОДНОРАЗОВЫЙ: после отправки
   изменить его нельзя (только школа может удалить заявку), поэтому
   отправленная заявка показывается компактной свёрнутой плашкой —
   разворачивается только для просмотра списка. */
function SelectionCard({ selection, studentId, onSaved }) {
  const { period, subjects } = selection;
  const submitted = !!selection.savedAt;

  const [picked, setPicked]     = useState(() => new Set(selection.selected || []));
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null); // { ok, text }
  // Отправленная заявка по умолчанию свёрнута — не занимает экран.
  const [expanded, setExpanded] = useState(false);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setMsg(null);
  };

  const min = period.selectionMin || 0;
  const max = period.selectionMax || 0;
  const n = picked.size;
  const limitText = min > 0 && max > 0
    ? (min === max ? `нужно выбрать ровно ${min}` : `нужно выбрать от ${min} до ${max}`)
    : min > 0 ? `нужно выбрать не меньше ${min}`
      : max > 0 ? `можно выбрать не больше ${max}` : '';
  const outOfRange = (min > 0 && n < min) || (max > 0 && n > max);

  const save = async () => {
    if (saving || outOfRange) return;
    if (!window.confirm('Отправить выбор? Изменить его после отправки будет нельзя.')) return;
    setSaving(true); setMsg(null);
    try {
      const res = await api.post('/api/parent/extracurricular-selection', {
        studentId,
        periodId:   period.id,
        subjectIds: Array.from(picked),
      });
      onSaved?.(res); // родитель обновит savedAt → карточка свернётся сама
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Не удалось сохранить' });
    } finally {
      setSaving(false);
    }
  };

  // ── Заявка отправлена: компактная плашка (по клику — просмотр) ──────
  if (submitted) {
    const chosen = subjects.filter((s) => (selection.selected || []).includes(s.id));
    return (
      <div className="pc-card" style={{ borderColor: 'var(--pc-ok, #15803d)' }}>
        <div className="pc-card__head" style={{ cursor: 'pointer' }}
             onClick={() => setExpanded((v) => !v)}>
          <div className="pc-card__title" style={{ fontSize: 14 }}>
            <span style={{ color: 'var(--pc-ok, #15803d)' }}>✓</span>{' '}
            Выбор внеурочных занятий отправлен · {chosen.length}
          </div>
          <span className="pc-card__sub" style={{ cursor: 'pointer' }}>
            {expanded ? 'Свернуть ▲' : 'Показать ▼'}
          </span>
        </div>
        {expanded && (
          <div className="pc-card__body" style={{ paddingTop: 0 }}>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              {chosen.map((s) => <div key={s.id}>• {s.name}</div>)}
            </div>
            <div style={{ fontSize: 12, color: 'var(--pc-fg-faint)', marginTop: 8 }}>
              Отправлено {String(selection.savedAt).slice(0, 16)}. Выбор одноразовый —
              если нужно что-то изменить, обратитесь в школу.
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Заявка ещё не подана: полная форма выбора ────────────────────────
  return (
    <div className="pc-card" style={{ borderColor: 'var(--pc-accent, #16a34a)' }}>
      <div className="pc-card__head">
        <div className="pc-card__title">Выбор внеурочных занятий</div>
        <span className="pc-card__sub">приём заявок до {fmtDateRu(period.dateEnd)}</span>
      </div>
      <div className="pc-card__body">
        {period.comment ? (
          // HTML санитизирован на бэке (белый список тегов при сохранении периода).
          <div style={{ fontSize: 13.5, lineHeight: 1.5, marginBottom: 12 }}
               dangerouslySetInnerHTML={{ __html: period.comment }} />
        ) : null}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '6px 16px',
          marginBottom: 12,
        }}>
          {subjects.map((s) => (
            <label key={s.id}
                   style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={picked.has(s.id)}
                     onChange={() => toggle(s.id)} disabled={saving} />
              <span>{s.name}</span>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn--primary btn--sm"
                  onClick={save} disabled={saving || outOfRange}>
            {saving ? 'Отправляем…' : 'Отправить выбор'}
          </button>
          <span style={{ fontSize: 12.5, color: outOfRange ? 'var(--pc-warn, #d97706)' : 'var(--pc-fg-faint)' }}>
            Выбрано: {n}{limitText ? ` · ${limitText}` : ''}
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--pc-fg-faint)', marginTop: 8 }}>
          Внимательно проверьте список: выбор одноразовый, изменить его после
          отправки нельзя.
        </div>

        {msg && (
          <div style={{
            marginTop: 10, fontSize: 12.5,
            color: msg.ok ? 'var(--pc-ok, #15803d)' : 'var(--pc-warn, #d97706)',
          }}>
            {msg.text}
          </div>
        )}
      </div>
    </div>
  );
}
